'use client';

import clsx from 'clsx';

import {
  ComponentPropsWithoutRef,
  FC,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useInterval } from 'react-use';

import { Loader } from '@/components/momentum/components/loader';

import styles from './iframe-embed.module.css';

export interface IFrameEmbedProps
  extends Omit<
    ComponentPropsWithoutRef<'iframe'>,
    'onLoad' | 'width' | 'scrolling' | 'frameBorder'
  > {
  height?: number;
}

export const IFrameEmbed: FC<IFrameEmbedProps> = ({
  height,
  ...props
}: IFrameEmbedProps) => {
  const ref = useRef<HTMLIFrameElement>(null);

  const [iframeHeight, setIframeHeight] = useState<number>(height ?? 200);
  const [loaded, setLoaded] = useState<boolean>(false);

  useInterval(() => {
    if (!height && ref.current?.contentWindow) {
      const { contentWindow } = ref.current;
      setIframeHeight(contentWindow.document.body?.scrollHeight);
    }
  }, 200);

  const onLoad = useCallback(() => {
    if (ref.current) {
      setLoaded(true);
    }
  }, [ref]);

  return (
    <div
      className={clsx({
        [styles['embed']]: true,
        [styles['embed--loaded']]: loaded,
      })}
    >
      <div className={styles['embed__container']}>
        <iframe
          ref={ref}
          onLoad={onLoad}
          width='100%'
          height={`${iframeHeight}px`}
          scrolling='no'
          frameBorder='0'
          {...props}
        ></iframe>
      </div>
      {!loaded && (
        <div className={styles['embed__loader']}>
          <Loader />
        </div>
      )}
    </div>
  );
};
